/* eslint-disable-line quotes */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';

import { useApolloClient } from '@apollo/client';
import TextInput from '../../../TextInput/TextInput';
import PasswordInput from '../../../PasswordInput/PasswordInput';
import * as Styled from './LoginForm.styled';
import Button from '../../../Button/Button';
import { post } from '../../../../util/request';
import { platform_base_url } from '../../../../util/config';
import { completeAuthentication } from '../../Authentication.utils';
import FacebookLoginButton from '../FacebookLoginButton/FacebookLoginButton';
import GoogleSignInButton from '../GoogleSignInButton/GoogleSignInButton';
import { SocialContainer } from '../AuthenticationBody/AuthenticationBody.styled';

const PASSWORD_RESET_REQUIRED = 303;

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address.')
    .required('Required'),
  password: Yup.string('Please enter a password.').required('Required')
});

/**
 * `LoginForm` allows a user to log in to their Bombas account.
 */
const LoginForm = ({
  onComplete,
  onForgotPasswordClick,
  onSignUpClick,
  onPasswordResetRequired,
  perks
}) => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState(false);

  const handleSubmit = async (
    { email, password },
    { validateForm, setErrors }
  ) => {
    setIsLoading(true);
    setServerError(false);

    const formErrors = await validateForm();
    if (Object.keys(formErrors).length) {
      setErrors(formErrors);
      setIsLoading(false);
      return;
    }

    await post(
      `${platform_base_url}/v2/identity/sign-in`,
      { email, password },
      { credentials: 'include' }
    )
      .then(completeAuthentication.bind(null, client))
      .then(onComplete.bind(null))
      .catch((e) => {
        const error = e?.response?.errors?.[0];

        if (error?.status === PASSWORD_RESET_REQUIRED) {
          post(`${platform_base_url}/v2/identity/forgot-password`, {
            email
          })
            .then(() => {
              onPasswordResetRequired(email);
            })
            .catch(() => {
              setServerError(true);
            });
        } else {
          setErrors({
            email: true,
            password: 'Incorrect email and/or password'
          });

          window.DD_RUM?.addError(e, { location: 'identity/sign-in' });
        }
        setIsLoading(false);
      });
  };

  return (
    <Styled.LoginForm>
      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={LoginSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        <Form>
          <Styled.FormWrap>
            <Styled.DescriptionText>
              Log in for{' '}
              {perks && (
                <>
                  <Styled.Bold>{perks.join(', ')}</Styled.Bold>,{' '}
                </>
              )}
              faster checkout and access to your order history.
            </Styled.DescriptionText>
            <TextInput
              id="login_email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
            />
            <PasswordInput
              id="login_pass"
              name="password"
              label="Password"
              autoComplete="password"
            />
            <Styled.TextLink type="button" onClick={onForgotPasswordClick}>
              Forgot Password?
            </Styled.TextLink>
            {serverError && (
              <Styled.NotificationError
                variant="error"
                title="Something went wrong on our end."
                body="Please try submitting the form again."
                isFull
                onRequestClose={() => setServerError(false)}
              />
            )}
            <Button
              type="submit"
              variant="primary"
              isFull
              size="large"
              isLoading={isLoading}
            >
              Log In
            </Button>
          </Styled.FormWrap>
        </Form>
      </Formik>
      <Styled.OrText>or</Styled.OrText>

      <SocialContainer>
        <FacebookLoginButton
          onComplete={onComplete}
          setIsLoading={setIsLoading}
        />
        <GoogleSignInButton
          onComplete={onComplete}
          setIsLoading={setIsLoading}
        />
      </SocialContainer>

      <Styled.SignUpText>
        {/* eslint-disable quotes */}
        {"Don't Have An Account? "}
        {/* eslint-enable quotes */}
        <Styled.SignUpLink onClick={onSignUpClick}>Sign Up</Styled.SignUpLink>
      </Styled.SignUpText>
    </Styled.LoginForm>
  );
};

LoginForm.propTypes = {
  /**
   * Callback on successful submission of sign up form
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * Callback on click on forgot password link
   */
  onForgotPasswordClick: PropTypes.func.isRequired,
  /**
   * Callback for click on sign up link
   */
  onSignUpClick: PropTypes.func.isRequired,
  /**
   * Callback function to run when a password reset is required to authenticate
   */
  onPasswordResetRequired: PropTypes.func.isRequired,
  /**
   * List of perks that are offered by logging in
   */
  perks: PropTypes.arrayOf(PropTypes.string)
};

LoginForm.defaultProps = {
  perks: null
};

LoginForm.displayName = 'LoginForm';

export default LoginForm;
